import React, { useState, useCallback, useEffect } from 'react';
import { Pagination, Button, message, Modal } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useHashState } from '../../utils/hashState';
import { OrderType } from '../../graphql/types/OrderType';
import { useOrderCloseQuery } from './hooks/useOrderCloseQuery';
import { OrderCloseFiltersInput } from './graphql/queries/getPendingCloseOrders';
import OrderCloseFilter from './components/OrderCloseFilter';
import { OrderCloseColumns } from './components/OrderCloseColumns';
import CentralParcelDetailDrawer from '../central/parcels/components/CentralParcelDetailDrawer';
import { getUserInfo, hasHeadquartersAccess, isHubManager, isWarehouseManager } from '../../utils/auth';
import { parseException } from '../../utils/exceptionUtils';
import { t } from '../../i18n';
import { useApolloClient, useMutation } from '@apollo/client';
import { MARK_AS_EXCEPTION } from '../../graphql/mutations/markAsException';
import { SET_RETURNED_STATUS } from '../../graphql/mutations/setReturnedStatus';
import BatchMarkAsExceptionModal, { BatchMarkExceptionInput } from '../../components/BatchMarkAsExceptionModal';
import SetReturnStatusModal, { SetReturnStatusData } from '../../components/SetReturnStatusModal';
import { useSetDeliverStatus } from '../../hooks/useSetDeliverStatus';
import SetDeliverStatusModal from '../../components/SetDeliverStatusModal';
import FailedOrdersModal from '../../components/FailedOrdersModal';
import CloseOrderModal from './components/CloseOrderModal';
import BatchCloseOrderModal, { BatchCloseOrderInput } from './components/BatchCloseOrderModal';
import { filterExceptionByUserRole } from '../../config/roleExceptionConfig';
import { useQuery } from '@apollo/client';
import {
    ExceptionDictionaryResponse,
    ExceptionDictionaryType,
    GET_ALL_EXCEPTION
} from '../delivery/parcels/graphql/queries/getAllException';
import { useOrderCloseDownload } from './hooks/useOrderCloseDownload';
import OrderCloseDownloadModal from './components/OrderCloseDownloadModal';
import { CLOSE_ORDER } from './graphql/mutations/closeOrder';
import { VALIDATE_CLOSE_OPERATION, ValidateCloseOperationResponse } from './graphql/queries/validateCloseOperation';

const OrderClosePage: React.FC = () => {
    const apolloClient = useApolloClient();
    const [detailVisible, setDetailVisible] = useState(false);
    const [orderId, setOrderId] = useState<string>('');
    const [tempFilters, setTempFilters] = useHashState({});
    const [filters, setFilters] = useHashState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<OrderType[]>([]);

    // 设置页面标题
    useEffect(() => {
        document.title = t('orderClose.title');
    }, [t]);
    
    // 操作按钮相关状态
    const [batchModalVisible, setBatchModalVisible] = useState(false);
    const [batchMarkLoading, setBatchMarkLoading] = useState(false);
    const [returnStatusVisible, setReturnStatusVisible] = useState(false);
    const [returnStatusLoading, setReturnStatusLoading] = useState(false);
    const [closeOrderModalVisible, setCloseOrderModalVisible] = useState(false);
    const [closeOrderModalOrder, setCloseOrderModalOrder] = useState<OrderType | null>(null);
    const [batchCloseOrderModalVisible, setBatchCloseOrderModalVisible] = useState(false);
    const [batchCloseOrderLoading, setBatchCloseOrderLoading] = useState(false);
    const [orderCloseDownloadModalVisible, setOrderCloseDownloadModalVisible] = useState(false);
    const [orderCloseDownloadFileName, setOrderCloseDownloadFileName] = useState('');

    const {
        parcelData,
        loading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useOrderCloseQuery(filters, currentPage, pageSize);

    // 下载功能（提交成功后弹出弹窗）
    const { downloadLoading, downloadOrders } = useOrderCloseDownload({
        onSubmitted: fileName => {
            setOrderCloseDownloadFileName(fileName);
            setOrderCloseDownloadModalVisible(true);
        },
    });

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    // 添加useEffect监听状态变化后执行查询（与总部包裹查询页面保持一致）
    useEffect(() => {
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize]);

    const handleResetFilters = () => {
        // 直接清空 URL hash，然后触发 hashchange 事件让 useHashState 的监听器处理状态更新
        const currentUrl = window.location.pathname + window.location.search;
        window.history.replaceState(null, '', currentUrl);
        // 手动触发 hashchange 事件，确保 useHashState 的监听器能够响应
        window.dispatchEvent(new HashChangeEvent('hashchange'));
        // 强制清空状态（作为备用方案）
        setTempFilters({});
        setFilters({});
        setCurrentPage(1);
        // 清空列表勾选状态
        setSelectedRowKeys([]);
        setSelectedRows([]);
    };

    const handlePageChange = (page: number, size?: number) => {
        setCurrentPage(page);
        if (size) {
            setPageSize(size);
        }
    };

    const showParcelDetail = (record: OrderType) => {
        setOrderId(record.id?.toString() || '');
        setDetailVisible(true);
    };

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: OrderType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const userRole = getUserInfo()?.account?.role?.name || '';
    const { data: exceptionData } = useQuery<ExceptionDictionaryResponse>(GET_ALL_EXCEPTION);
    const exceptionList = exceptionData?.getExceptionDictionaryForHeadquarters || [];
    const exceptionSelectData: ExceptionDictionaryType[] = filterExceptionByUserRole(exceptionList, userRole, undefined);
    
    const [markAsException] = useMutation(MARK_AS_EXCEPTION);
    const [setReturnedStatus] = useMutation(SET_RETURNED_STATUS);
    const [closeOrder] = useMutation(CLOSE_ORDER);
    
    // 检查是否有权限操作设置送达状态功能
    const hasSetDeliverStatusPermission = hasHeadquartersAccess() || isHubManager() || isWarehouseManager();
    
    // 使用公共hook
    const {
        deliverStatusVisible,
        deliverStatusLoading,
        filteredDeliverRows,
        handleSetDeliverStatus,
        handleSetDeliverStatusConfirm,
        setDeliverStatusVisible,
        failedOrdersVisible,
        failedOrders,
        setFailedOrdersVisible,
        setFailedOrders
    } = useSetDeliverStatus({
        warehouseId: null,
        handleFilter: handleFilter,
        onClearSelection: () => {
            setSelectedRowKeys([]);
            setSelectedRows([]);
        }
    });

    // 复制运单号
    const copyTrackingNumbers = useCallback(async () => {
        if (!selectedRows || selectedRows.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const trackingNumbers = selectedRows
            .map(row => row.trackingNumber?.trim())
            .filter((tracking): tracking is string => Boolean(tracking && tracking.length > 0));
        if (trackingNumbers.length === 0) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        const text = trackingNumbers.join('\n');
        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.opacity = '0';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            message.success(t('common.messages.copied'));
        } catch (error) {
            console.error('Copy tracking numbers failed:', error);
            message.error(t('common.messages.copyFailed'));
        }
    }, [selectedRows, t]);

    // 标记异常
    const handleMarkAsException = () => {
        if (selectedRows.length < 1) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        setBatchModalVisible(true);
    };

    const handleBatchMarkOk = async (orders: BatchMarkExceptionInput[]) => {
        try {
            setBatchMarkLoading(true);
            await Promise.all(orders.map(order =>
                markAsException({
                    variables: {
                        orderId: order.orderId,
                        exceptionReasonCode: order.exceptionReasonCode,
                        exceptionHubId: order.exceptionHubId,
                        exceptionWarehouseId: order.exceptionWarehouseId,
                        exceptionImageS3KeysJson: order.exceptionImageS3KeysJson || []
                    },
                    refetchQueries: ['GetPendingCloseOrders']
                })
            ));
            message.success(t('central.parcels.messages.markingExceptionSuccessfully'));
            setBatchModalVisible(false);
            handleFilter();
        } catch (error) {
            console.error('Marking exception failed:', error);
            message.error(t('central.parcels.messages.markingExceptionFailed'));
        } finally {
            setBatchMarkLoading(false);
        }
    };

    // 设置退件状态
    const handleSetReturnStatus = () => {
        if (selectedRows.length < 1) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        // 检查是否所有选中的包裹都是退件相关状态
        if (!selectedRows.every(row =>
            row.overallStatus === 'RETURN_ARRIVED_DESTINATION_HUB' ||
            row.overallStatus === 'RETURN_STARTED'
        )) {
            message.warning(t('central.parcels.messages.selectParcelsWithReturnDirection'));
            return;
        }
        setReturnStatusVisible(true);
    };

    const handleSetReturnStatusConfirm = async (formData: SetReturnStatusData) => {
        try {
            setReturnStatusLoading(true);
            await setReturnedStatus({
                variables: {
                    orderIds: formData.orderIds,
                    returnedImageS3KeysJson: formData.returnedImageS3KeysJson
                }
            });
            message.success(t('central.parcels.messages.setReturnStatusSuccessfully'));
            setReturnStatusVisible(false);
            handleFilter();
        } catch (error) {
            console.error('set return status failed:', error);
            const { message: errorMessage } = parseException(error);
            message.error(errorMessage);
        } finally {
            setReturnStatusLoading(false);
        }
    };

    // 操作关单（单个订单）
    const handleCloseOrder = (record: OrderType) => {
        setCloseOrderModalOrder(record);
        setCloseOrderModalVisible(true);
    };

    // 批量操作关单（多个订单）
    const handleBatchCloseOrder = () => {
        if (selectedRows.length < 1) {
            message.warning(t('central.parcels.messages.selectAtLeastOneParcel'));
            return;
        }
        // 单个订单使用单个关单模态框
        if (selectedRows.length === 1) {
            handleCloseOrder(selectedRows[0]);
        } else {
            // 多个订单使用批量关单模态框
            setBatchCloseOrderModalVisible(true);
        }
    };

    // 批量关单确认处理
    /**
     * 将后端返回的错误消息转换为多语言消息
     */
    const getLocalizedErrorMessage = (errorMessage: string | null | undefined): string => {
        if (!errorMessage) {
            return t('orderClose.closeFailed');
        }

        // 错误消息到翻译键的映射
        const errorMessageMap: Record<string, string> = {
            'Delivery failed can only be applied to orders in DELIVERY_IN_PROGRESS status': 'orderClose.validationErrors.deliveryFailedInvalidStatus',
            'Return scan can only be applied to orders with RETURN next action': 'orderClose.validationErrors.returnScanInvalidNextAction',
            'Return scan requires return warehouse scan completed': 'orderClose.validationErrors.returnScanRequiresWarehouseScan',
            'Returned can only be applied to orders with RETURN_SCAN event record': 'orderClose.validationErrors.returnedRequiresReturnScanEvent',
            'Exception reason code is required for LOST close type': 'orderClose.validationErrors.lostRequiresExceptionReason',
            'Order has already been closed. Cannot close the same order twice.': 'orderClose.validationErrors.alreadyClosed',
        };

        // 处理包含动态参数的错误消息（如订单ID）
        if (errorMessage.startsWith('Order not found with id: ')) {
            const orderId = errorMessage.replace('Order not found with id: ', '');
            return t('orderClose.validationErrors.orderNotFound', { orderId });
        }

        // 查找对应的翻译键
        const translationKey = errorMessageMap[errorMessage];
        if (translationKey) {
            const translated = t(translationKey);
            // 如果翻译键不存在，返回原始消息
            return translated !== translationKey ? translated : errorMessage;
        }

        // 如果没有找到对应的翻译键，返回原始消息
        return errorMessage;
    };

    const handleBatchCloseOrderOk = async (orders: BatchCloseOrderInput[]) => {
        setBatchCloseOrderLoading(true);
        // 先做后端规则校验：不满足条件的订单直接计入失败，避免打到关单接口产生服务端 ERROR 日志
        const validationResults = await Promise.allSettled(
            orders.map(order =>
                apolloClient.query<ValidateCloseOperationResponse>({
                    query: VALIDATE_CLOSE_OPERATION,
                    variables: {
                        orderId: order.orderId.toString(),
                        closeType: order.closeType,
                    },
                    fetchPolicy: 'no-cache',
                })
            )
        );

        const successful: Array<{ orderId: number; trackingNumber?: string }> = [];
        const failed: Array<{ orderId: number; trackingNumber?: string; error: string }> = [];

        const validOrders: BatchCloseOrderInput[] = [];
        validationResults.forEach((result, index) => {
            const order = orders[index];
            const trackingNumber = selectedRows.find(row => row.id === order.orderId.toString())?.trackingNumber;

            if (result.status === 'fulfilled') {
                const validation = result.value.data.validateCloseOperation;
                if (validation.isValid) {
                    validOrders.push(order);
                } else {
                    failed.push({
                        orderId: order.orderId,
                        trackingNumber,
                        error: getLocalizedErrorMessage(validation.errorMessage),
                    });
                }
            } else {
                const errorMessage = result.reason?.message || result.reason?.graphQLErrors?.[0]?.message || String(result.reason);
                failed.push({
                    orderId: order.orderId,
                    trackingNumber,
                    error: getLocalizedErrorMessage(errorMessage),
                });
            }
        });

        const closeResults = await Promise.allSettled(
            validOrders.map(order =>
                closeOrder({
                    variables: {
                        input: {
                            orderId: order.orderId.toString(),
                            closeType: order.closeType,
                            exceptionReasonCode: order.exceptionReasonCode,
                            exceptionPriority: order.exceptionPriority || 100,
                            imageS3Keys: order.imageS3Keys,
                            exceptionHubId: order.exceptionHubId,
                            exceptionWarehouseId: order.exceptionWarehouseId
                        }
                    },
                    refetchQueries: ['GetPendingCloseOrders']
                })
            )
        );

        closeResults.forEach((result, index) => {
            const order = validOrders[index];
            const trackingNumber = selectedRows.find(row => row.id === order.orderId.toString())?.trackingNumber;
            
            if (result.status === 'fulfilled') {
                successful.push({ orderId: order.orderId, trackingNumber });
            } else {
                const errorMessage = result.reason?.message || result.reason?.graphQLErrors?.[0]?.message || String(result.reason);
                failed.push({ 
                    orderId: order.orderId, 
                    trackingNumber,
                    error: getLocalizedErrorMessage(errorMessage)
                });
            }
        });

        // 显示结果统计
        if (successful.length > 0 && failed.length === 0) {
            // 全部成功
            message.success(t('orderClose.batchCloseSuccess'));
            setBatchCloseOrderModalVisible(false);
            setSelectedRowKeys([]);
            setSelectedRows([]);
            handleFilter();
        } else if (successful.length > 0 && failed.length > 0) {
            // 部分成功
            const successMsg = t('orderClose.batchClosePartialSuccessMessage', {
                successCount: successful.length,
                failedCount: failed.length
            });
            message.warning(successMsg);
            
            // 显示失败订单详情
            const failedDetails = failed.map(f => 
                `${t('orderClose.trackingNumber')} ${f.trackingNumber || f.orderId}: ${f.error}`
            ).join('\n');
            Modal.warning({
                title: t('orderClose.batchClosePartialSuccess'),
                content: (
                    <div>
                        <p>{t('orderClose.batchClosePartialSuccessMessage', {
                            successCount: successful.length,
                            failedCount: failed.length
                        })}</p>
                        <pre style={{ maxHeight: '300px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                            {failedDetails}
                        </pre>
                    </div>
                ),
                width: 600,
            });
            
            // 刷新列表以更新成功关单的状态
            handleFilter();
        } else {
            // 全部失败
            const failedDetails = failed.map(f => 
                `${t('orderClose.trackingNumber')} ${f.trackingNumber || f.orderId}: ${f.error}`
            ).join('\n');
            Modal.error({
                title: t('orderClose.batchCloseAllFailed'),
                content: (
                    <div>
                        <p>{t('orderClose.batchCloseAllFailedMessage', {
                            totalCount: failed.length
                        })}</p>
                        <pre style={{ maxHeight: '300px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
                            {failedDetails}
                        </pre>
                    </div>
                ),
                width: 600,
            });
        }
        
        setBatchCloseOrderLoading(false);
    };

    return (
        <div className="bg-white rounded-lg shadow-sm p-6">
            {/* 筛选条件 */}
            <OrderCloseFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                handleFilter={handleFilter}
                handleResetFilters={handleResetFilters}
                selectedRows={selectedRows}
            />

            {/* 操作按钮区域 - 左右对齐 */}
            <div className="mb-4 flex justify-between">
                <div className="flex" style={{ gap: '8px' }}>
                    <Button type="primary" onClick={handleMarkAsException}>
                        {t('central.parcels.buttons.markAsException')}
                    </Button>
                    <Button type="primary" onClick={handleSetReturnStatus}>
                        {t('central.parcels.buttons.setReturnStatus')}
                    </Button>
                    {hasSetDeliverStatusPermission && (
                        <Button type="primary" onClick={() => handleSetDeliverStatus(selectedRows)}>
                            {t('central.parcels.buttons.setDeliverStatus')}
                        </Button>
                    )}
                    <Button type="primary" onClick={handleBatchCloseOrder}>
                        {t('orderClose.operateClose')}
                    </Button>
                </div>

                <div className="flex" style={{ gap: '8px' }}>
                    <Button type="primary" onClick={copyTrackingNumbers}>
                        {t('common.buttons.copyTracking')}
                    </Button>
                    <Button
                        type="primary"
                        onClick={() => downloadOrders(filters)}
                        loading={downloadLoading}
                        icon={<DownloadOutlined />}
                    >
                        {t('components.button.download')}
                    </Button>
                    <Button type="primary" onClick={handleFilter}>
                        {t('components.button.filter')}
                    </Button>
                    <Button onClick={handleResetFilters}>
                        {t('components.button.reset')}
                    </Button>
                </div>
            </div>

            {/* 订单列表 */}
            <OrderCloseColumns
                parcelData={parcelData}
                showParcelDetail={showParcelDetail}
                onSelectChange={handleSelectChange}
                selectedRowKeys={selectedRowKeys}
                loading={loading}
                onCloseOrder={handleCloseOrder}
            />

            {/* 分页 */}
            <div className="flex justify-between items-center mt-4">
                <div className="text-gray-500">{totalElements} {t('components.pagination.results')}</div>
                <Pagination
                    current={currentPage}
                    total={totalElements}
                    pageSize={pageSize}
                    size="small"
                    showSizeChanger
                    onChange={handlePageChange}
                />
            </div>

            {/* 包裹详情抽屉组件 */}
            <CentralParcelDetailDrawer
                visible={detailVisible}
                onClose={() => setDetailVisible(false)}
                orderId={orderId}
                userRole={getUserInfo()?.account?.role?.name || ''}
                onRefreshList={() => refetch(buildQueryVariables())}
                hideEditActions={false}
                hidePrintAndPodReview={true}
                extraActionButtons={
                    <Button
                        type="primary"
                        onClick={() => {
                            const order = parcelData.find(p => p.id?.toString() === orderId);
                            if (order) {
                                handleCloseOrder(order);
                            }
                        }}
                    >
                        {t('orderClose.operateClose')}
                    </Button>
                }
            />

            {/* 批量标记异常弹窗 */}
            <BatchMarkAsExceptionModal
                visible={batchModalVisible}
                parcels={selectedRows.map(row => ({
                    orderId: Number(row.id),
                    trackingNumber: row.trackingNumber,
                    existingImageUrls: row.exceptionImageS3UrlsJson || [],
                    exceptionHubId: row.exceptionHubId,
                    exceptionWarehouseId: row.exceptionWarehouseId
                }))}
                onOk={handleBatchMarkOk}
                onCancel={() => setBatchModalVisible(false)}
                loading={batchMarkLoading}
                userRole={userRole}
                exceptionSelectData={exceptionSelectData}
            />

            {/* 设置退件状态弹窗 */}
            <SetReturnStatusModal
                visible={returnStatusVisible}
                onClose={() => setReturnStatusVisible(false)}
                onConfirm={handleSetReturnStatusConfirm}
                loading={returnStatusLoading}
                parcels={selectedRows}
            />

            {/* 设置妥投状态弹窗 */}
            <SetDeliverStatusModal
                visible={deliverStatusVisible}
                onClose={() => setDeliverStatusVisible(false)}
                onConfirm={handleSetDeliverStatusConfirm}
                loading={deliverStatusLoading}
                parcels={filteredDeliverRows}
            />

            {/* 失败订单弹窗 */}
            <FailedOrdersModal
                visible={failedOrdersVisible}
                onClose={() => setFailedOrdersVisible(false)}
                failedOrders={failedOrders}
            />

            {/* 操作关单弹窗 */}
            <CloseOrderModal
                visible={closeOrderModalVisible}
                onClose={() => {
                    setCloseOrderModalVisible(false);
                    setCloseOrderModalOrder(null);
                }}
                order={closeOrderModalOrder}
                onSuccess={() => {
                    setCloseOrderModalVisible(false);
                    setCloseOrderModalOrder(null);
                    if (closeOrderModalOrder) {
                        handleFilter();
                    }
                }}
            />

            {/* 批量操作关单弹窗 */}
            <BatchCloseOrderModal
                visible={batchCloseOrderModalVisible}
                parcels={selectedRows.map(row => ({
                    orderId: Number(row.id),
                    trackingNumber: row.trackingNumber || ''
                }))}
                onOk={handleBatchCloseOrderOk}
                onCancel={() => setBatchCloseOrderModalVisible(false)}
                loading={batchCloseOrderLoading}
            />

            <OrderCloseDownloadModal
                visible={orderCloseDownloadModalVisible}
                onClose={() => setOrderCloseDownloadModalVisible(false)}
                fileName={orderCloseDownloadFileName}
            />
        </div>
    );
};

export default OrderClosePage;
